import type { DateTime } from "luxon";

import type { LocalAudioRecordingTranscriptTurnRecord } from "@/domains/dexie-db/models/audio-recording/transcripts";
import type {
  TranscriptArrayItem,
  TranscriptArraySpeakerParticipant,
  TranscriptSpeakerIdentificationContext,
  TranscriptSpeakerIdentificationReason,
  TranscriptSpeakerAudioSource,
  TranscriptSpeakerParticipantMetadata,
  TranscriptSpeakerVariant,
} from "@/modules/audio-recording/transcript-types";
import {
  parseOptionalTranscriptSpeakerVariant,
  parseTranscriptSpeakerAudioSource,
  parseTranscriptSpeakerIdentificationContext,
  parseTranscriptSpeakerIdentificationReason,
} from "@/modules/audio-recording/transcript-speaker-identity";
import {
  isRecallNonIdentityParticipantName,
  normalizeSpeakerParticipantName,
  resolveRecallNonIdentitySpeakerLabelFromExtraData,
  resolveTranscriptSpeakerDisplayLabel,
  sanitizeRecallSpeakerLabelExtraData,
} from "@/modules/audio-recording/transcript-speaker-labels";
import { resolveTranscriptSpeakerIsPlatformHost } from "@/modules/audio-recording/transcript-speaker-platform-host";

const toISOString = (dt: DateTime | undefined | null): string | undefined => {
  if (dt && typeof dt.toISO === "function") return dt.toISO() ?? undefined;
  return undefined;
};

const buildTranscriptArraySpeakerParticipant = (
  metadata: TranscriptSpeakerParticipantMetadata | null | undefined
): TranscriptArraySpeakerParticipant | undefined => {
  if (!metadata) return undefined;

  const participant: TranscriptArraySpeakerParticipant = {};
  const rawName = normalizeSpeakerParticipantName(metadata.name);
  const isNonIdentityName = rawName != null && isRecallNonIdentityParticipantName(rawName);
  const email = normalizeSpeakerParticipantName(metadata.email);
  const hasDisplayableIdentityLabel = Boolean(rawName && !isNonIdentityName) || Boolean(email);
  const hasNonIdentityExtraDataLabel =
    !hasDisplayableIdentityLabel &&
    resolveRecallNonIdentitySpeakerLabelFromExtraData(metadata.extraData) !== undefined;
  const hasNonIdentitySpeakerLabel = isNonIdentityName || hasNonIdentityExtraDataLabel;
  const id = !hasNonIdentitySpeakerLabel ? normalizeParticipantIdentifier(metadata.id) : undefined;
  if (id != null) {
    participant.id = id;
  }
  const name = normalizeSpeakerParticipantName(metadata?.name);
  if (name && !isRecallNonIdentityParticipantName(name)) {
    participant.name = name;
  }
  const isPlatformHost = resolveTranscriptSpeakerIsPlatformHost({ source: metadata });
  if (!hasNonIdentitySpeakerLabel && isPlatformHost != null)
    participant.is_platform_host = isPlatformHost;
  if (!hasNonIdentitySpeakerLabel && metadata?.platform != null)
    participant.platform = metadata.platform;
  if (email != null) participant.email = email;
  const sanitizedExtraData = sanitizeRecallSpeakerLabelExtraData(metadata.extraData);
  if (!hasNonIdentitySpeakerLabel && sanitizedExtraData != null)
    participant.extra_data = sanitizedExtraData;

  const hasIdentity =
    participant.id != null || participant.name != null || participant.email != null;
  return hasIdentity ? participant : undefined;
};

function normalizeParticipantIdentifier(value: unknown): number | string | undefined {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  return normalizeSpeakerParticipantName(value);
}

export type TranscriptPayload = {
  transcriptArray: TranscriptArrayItem[];
  transcriptText: string;
};

export const buildTranscriptPayload = (
  records: LocalAudioRecordingTranscriptTurnRecord[]
): TranscriptPayload => {
  if (!records.length) {
    return { transcriptArray: [], transcriptText: "" };
  }

  const transcriptArray: TranscriptArrayItem[] = records.map(record => {
    const words = record.content.words ?? [];
    const transcriptFromWords = words
      .map(word => word.text)
      .filter(Boolean)
      .join(" ")
      .trim();
    const transcript = record.content.transcript?.trim() || transcriptFromWords;

    // Pad the turn index so lexicographic sorting preserves turn order.
    const sortKey = `${record.transcriptTurnIndex}`.padStart(6, "0");
    const speakerParticipant = buildTranscriptArraySpeakerParticipant(
      record.content.speakerParticipant
    );
    const speakerAudioSource = normalizeOptionalSpeakerAudioSource(
      record.content.speakerAudioSource
    );
    const speakerVariant = normalizeOptionalSpeakerVariant(record.content.speakerVariant);
    const speakerIdentificationContext = normalizeOptionalSpeakerIdentificationContext(
      record.content.speakerIdentificationContext
    );
    const speakerIdentificationReason = normalizeOptionalSpeakerIdentificationReason(
      record.content.speakerIdentificationReason
    );

    return {
      sort_key: sortKey,
      transcript,
      channel: record.channel ?? 0,
      timestamp: toISOString(record.content.turnTimestamp),
      ...(record.content.speakerId ? { speaker_id: record.content.speakerId } : {}),
      ...(speakerVariant ? { speaker_variant: speakerVariant } : {}),
      ...(speakerIdentificationContext
        ? { speaker_identification_context: speakerIdentificationContext }
        : {}),
      ...(speakerIdentificationReason
        ? { speaker_identification_reason: speakerIdentificationReason }
        : {}),
      ...(speakerAudioSource ? { speaker_audio_source: speakerAudioSource } : {}),
      ...(speakerParticipant ? { speaker_participant: speakerParticipant } : {}),
    };
  });

  const transcriptText = transcriptArray
    .map(formatTranscriptTextItem)
    .filter(Boolean)
    .join(" ")
    .trim();

  return { transcriptArray, transcriptText };
};

/**
 * Adds participant labels to the transcript text uploaded with the attachment.
 *
 * The structured `transcript_array` keeps the raw speaker metadata, but the
 * plain transcript text is what many attachment and backend flows inspect first.
 * Prefixing only real participant labels keeps channel-only recordings unchanged
 * while making meeting-window speaker attribution visible in that text.
 */
function formatTranscriptTextItem(item: TranscriptArrayItem): string {
  const transcript = item.transcript.trim();
  if (!transcript) return "";

  const speakerLabel = resolveTranscriptSpeakerDisplayLabel({
    channel: item.channel,
    speakerParticipant: item.speaker_participant,
    speakerLabel: item.speaker_label,
    speakerLabelKind: item.speaker_label_kind,
  });

  return speakerLabel.source === "participant"
    ? `[${speakerLabel.label}] ${transcript}`
    : transcript;
}

function normalizeOptionalSpeakerAudioSource(
  value: unknown
): TranscriptSpeakerAudioSource | undefined {
  if (value == null) return undefined;
  return parseTranscriptSpeakerAudioSource(value);
}

function normalizeOptionalSpeakerVariant(value: unknown): TranscriptSpeakerVariant | undefined {
  return parseOptionalTranscriptSpeakerVariant(value);
}

function normalizeOptionalSpeakerIdentificationContext(
  value: unknown
): TranscriptSpeakerIdentificationContext | undefined {
  if (value == null) return undefined;
  return parseTranscriptSpeakerIdentificationContext(value);
}

function normalizeOptionalSpeakerIdentificationReason(
  value: unknown
): TranscriptSpeakerIdentificationReason | undefined {
  if (value == null) return undefined;
  return parseTranscriptSpeakerIdentificationReason(value);
}
